/**
 * Advertiser caps — data layer (feature #147).
 *
 * Thin typed wrappers over `apiClient` that mirror the HTTP contract exposed
 * by `apps/api/src/routes/advertiser-caps.ts`:
 *
 *   - PUT   /advertiser-caps              upsert a cap row
 *   - GET   /advertiser-caps/:advertiser  list caps for an advertiser
 *   - POST  /advertiser-caps/increment    bump consumed by delta
 *   - POST  /advertiser-caps/check        non-mutating "would it fit?" probe
 *
 * No React imports so the module can be unit-tested by the page spec.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CapType = "conversions" | "payout_cents" | "clicks";
export type WindowKey = "daily" | "weekly" | "monthly";
export type CapStatus = "active" | "paused";

export interface AdvertiserCapRow {
	id: string;
	advertiser_id: string;
	campaign_id: string | null;
	cap_type: CapType;
	cap_limit: number;
	window_key: WindowKey;
	consumed: number;
	resets_at: number;
	status: CapStatus;
	created_at: number;
	updated_at: number;
}

export interface CapsListResponse {
	advertiser_id: string;
	caps: AdvertiserCapRow[];
}

export interface UpsertCapInput {
	advertiserId: string;
	campaignId: string | null;
	capType: CapType;
	capLimit: number;
	windowKey: WindowKey;
	status?: CapStatus;
}

// ---------------------------------------------------------------------------
// Constants + formatting
// ---------------------------------------------------------------------------

export const CAP_TYPES: readonly CapType[] = [
	"conversions",
	"payout_cents",
	"clicks",
];

export const WINDOW_KEYS: readonly WindowKey[] = [
	"daily",
	"weekly",
	"monthly",
];

export const CAP_TYPE_LABEL: Record<CapType, string> = {
	conversions: "Conversions",
	payout_cents: "Payout",
	clicks: "Clicks",
};

export const WINDOW_LABEL: Record<WindowKey, string> = {
	daily: "Daily",
	weekly: "Weekly",
	monthly: "Monthly",
};

/**
 * Format the "consumed / limit" string for a progress-bar row. Payout caps
 * render as a dollar string; the others render as plain integers with
 * `en-US` grouping.
 */
export function formatConsumedOverLimit(row: {
	cap_type: CapType;
	consumed: number;
	cap_limit: number;
}): string {
	if (row.cap_type === "payout_cents") {
		const used = (row.consumed / 100).toFixed(2);
		const cap = (row.cap_limit / 100).toFixed(2);
		return `$${used} / $${cap}`;
	}
	const fmt = (n: number) => n.toLocaleString("en-US");
	return `${fmt(row.consumed)} / ${fmt(row.cap_limit)}`;
}

/**
 * Map (consumed, cap_limit) to a progress percentage in [0, 100]. Returns 0
 * when the limit is non-positive to avoid NaN / Infinity leaks into the DOM.
 */
export function progressPercent(consumed: number, cap_limit: number): number {
	if (!(cap_limit > 0)) return 0;
	const raw = (consumed / cap_limit) * 100;
	if (!Number.isFinite(raw)) return 0;
	if (raw < 0) return 0;
	if (raw > 100) return 100;
	return raw;
}

/**
 * Traffic-light colour for the progress bar. Green < 50%, amber < 90%, red
 * otherwise. Tailwind class names, not raw hex, so brand tokens stay in one
 * place.
 */
export function progressColor(percent: number): string {
	if (percent >= 90) return "bg-red-500";
	if (percent >= 50) return "bg-amber-500";
	return "bg-green-500";
}

// ---------------------------------------------------------------------------
// Client-side validation for the cap edit form.
// ---------------------------------------------------------------------------

export interface CapFormValues {
	capType: CapType;
	capLimit: string; // from <input type="number"> value
	windowKey: WindowKey;
	status: CapStatus;
}

export interface CapFormParseOk {
	success: true;
	data: {
		capType: CapType;
		capLimit: number;
		windowKey: WindowKey;
		status: CapStatus;
	};
}

export interface CapFormParseErr {
	success: false;
	errors: Record<string, string>;
}

export type CapFormParseResult = CapFormParseOk | CapFormParseErr;

export const capFormSchema = {
	safeParse(input: Partial<CapFormValues>): CapFormParseResult {
		const errors: Record<string, string> = {};

		const capType = input.capType;
		if (capType !== "conversions" && capType !== "payout_cents" && capType !== "clicks") {
			errors.capType = "Select a cap type.";
		}

		const windowKey = input.windowKey;
		if (windowKey !== "daily" && windowKey !== "weekly" && windowKey !== "monthly") {
			errors.windowKey = "Select a window.";
		}

		const limitRaw = typeof input.capLimit === "string" ? input.capLimit.trim() : "";
		let limit = Number.NaN;
		if (limitRaw.length === 0) {
			errors.capLimit = "Limit is required.";
		} else {
			limit = Number(limitRaw);
			if (!Number.isFinite(limit) || !Number.isInteger(limit) || limit < 0) {
				errors.capLimit = "Limit must be a non-negative integer.";
			}
		}

		const status = input.status;
		if (status !== "active" && status !== "paused") {
			errors.status = "Select a status.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				capType: capType as CapType,
				capLimit: limit,
				windowKey: windowKey as WindowKey,
				status: status as CapStatus,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

const BASE = "/advertiser-caps";

export async function fetchAdvertiserCaps(
	advertiserId: string,
): Promise<AdvertiserCapRow[]> {
	if (!advertiserId) return [];
	try {
		const res = await apiClient.get<CapsListResponse | AdvertiserCapRow[]>(
			`${BASE}/${encodeURIComponent(advertiserId)}`,
		);
		if (Array.isArray(res)) return res;
		return res.caps ?? [];
	} catch (err) {
		if (
			err instanceof ApiError &&
			err.status >= 400 &&
			err.status < 500
		) {
			return [];
		}
		throw err;
	}
}

export interface UpsertCapResponse {
	cap: AdvertiserCapRow;
	created: boolean;
}

export async function upsertAdvertiserCap(
	input: UpsertCapInput,
): Promise<UpsertCapResponse> {
	const body = {
		advertiserId: input.advertiserId,
		campaignId: input.campaignId,
		capType: input.capType,
		limit: input.capLimit,
		window: input.windowKey,
		status: input.status ?? "active",
	};
	return apiClient.put<UpsertCapResponse>(BASE, body);
}

export interface IncrementCapInput {
	advertiserId: string;
	campaignId: string | null;
	capType: CapType;
	windowKey: WindowKey;
	delta?: number;
}

export interface IncrementCapResponse {
	cap: AdvertiserCapRow;
	exceeded: boolean;
}

export async function incrementAdvertiserCap(
	input: IncrementCapInput,
): Promise<IncrementCapResponse> {
	return apiClient.post<IncrementCapResponse>(`${BASE}/increment`, {
		advertiserId: input.advertiserId,
		campaignId: input.campaignId,
		capType: input.capType,
		window: input.windowKey,
		delta: input.delta ?? 1,
	});
}

export interface CheckCapInput extends IncrementCapInput {}

export interface CheckCapResponse {
	allowed: boolean;
	reason: "no_cap" | "within_limit" | "cap_exceeded" | "paused";
	consumed: number;
	limit: number | null;
	remaining: number | null;
	cap?: AdvertiserCapRow;
}

export async function checkAdvertiserCap(
	input: CheckCapInput,
): Promise<CheckCapResponse> {
	return apiClient.post<CheckCapResponse>(`${BASE}/check`, {
		advertiserId: input.advertiserId,
		campaignId: input.campaignId,
		capType: input.capType,
		window: input.windowKey,
		delta: input.delta ?? 1,
	});
}

// ---------------------------------------------------------------------------
// Grouping helpers (pure) — the page groups caps by campaign_id, with null
// collapsing to an "All campaigns" bucket that sorts first.
// ---------------------------------------------------------------------------

export interface CampaignGroup {
	campaignId: string | null;
	caps: AdvertiserCapRow[];
}

export function groupByCampaign(rows: AdvertiserCapRow[]): CampaignGroup[] {
	const map = new Map<string, CampaignGroup>();
	for (const row of rows) {
		const key = row.campaign_id ?? "__ALL__";
		let group = map.get(key);
		if (!group) {
			group = { campaignId: row.campaign_id, caps: [] };
			map.set(key, group);
		}
		group.caps.push(row);
	}
	// Sort: all-campaigns bucket first, then alpha by campaign id.
	const groups = Array.from(map.values());
	groups.sort((a, b) => {
		if (a.campaignId === null && b.campaignId !== null) return -1;
		if (a.campaignId !== null && b.campaignId === null) return 1;
		return (a.campaignId ?? "").localeCompare(b.campaignId ?? "");
	});
	// Within each group, sort caps by cap_type then window for stable UI.
	for (const g of groups) {
		g.caps.sort((a, b) => {
			if (a.cap_type !== b.cap_type) {
				return a.cap_type.localeCompare(b.cap_type);
			}
			return a.window_key.localeCompare(b.window_key);
		});
	}
	return groups;
}

export const advertiserCapsPaths = {
	caps: BASE,
	check: `${BASE}/check`,
	increment: `${BASE}/increment`,
} as const;
